import { useCallback } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/design-system/components/ui";
import { PortalChrome } from "../components/PortalChrome";
import type { PortalTabId } from "../components/PortalTabMatrix";
import { usePortalAuth } from "../PortalAuthContext";
import { dashboardPathFor } from "./RecipientDashboardPage";
// Firm data surfaces (PR-6).
import { BrokerageHomeTab } from "../tabs/BrokerageHomeTab";
import { BrokerageDealsTab } from "../tabs/BrokerageDealsTab";
import { BrokeragePaymentsTab } from "../tabs/BrokeragePaymentsTab";
import { BrokeragePaymentSetupTab } from "../tabs/BrokeragePaymentSetupTab";
import { BrokerageRosterTab } from "../tabs/BrokerageRosterTab";
import { BrokerageActivityTab } from "../tabs/BrokerageActivityTab";
import { PrequalificationTab } from "../tabs/PrequalificationTab";
// Family-B tabs — account-type-agnostic, scoped to the PortalUser; reused as-is.
import { RequestsTab } from "../tabs/RequestsTab";
import { DocumentsTab } from "../tabs/DocumentsTab";
import { FilesTab } from "../tabs/FilesTab";
import { NotificationsTab } from "../tabs/NotificationsTab";
import { AccountSettingsTab } from "../tabs/AccountSettingsTab";

// Brokerage / Sub-Broker portal — the EXTERNAL brokerage firm's console, landed
// at /portal/brokerage. A DEDICATED page (like LeadGeneratorDashboardPage /
// BrokerDashboardPage) rather than a RecipientDashboardPage arm: it uses
// PortalChrome for the branded shell + auth/disabled/forced-password guards, so
// the shared RecipientDashboardPage stays untouched.
//
// Navigation lives ENTIRELY in the shared black left rail (PortalChrome →
// PortalSidebar), driven by ?tab= — the one-nav pattern every external portal
// uses. PR-5 (this page) renders a placeholder body per tab; PR-6 fills in the
// real firm-production / roster / activity data surfaces.

// The firm's console sections, as PortalTabIds on the shared rail.
const BROKERAGE_TABS = [
  "home",
  "deals",
  "prequalification",
  "payments",
  "payment-setup",
  "roster",
  "activity",
  "requests",
  "documents",
  "files",
  "notifications",
  "settings",
] as const;
type BrokerageTab = (typeof BROKERAGE_TABS)[number];

const TAB_META: Record<BrokerageTab, { heading: string; description: string }> = {
  home: {
    heading: "Home",
    description: "Your firm at a glance — production, sub-brokers, and what needs attention.",
  },
  deals: {
    heading: "Deals & status",
    description: "The current status and stage of your firm's deals.",
  },
  // Partner Brand Ownership — the completions the firm's OWN prequalification
  // site produced. A member broker's separately-owned site is deliberately
  // never rolled up here (the locked privacy decision), and the server enforces
  // it — this page only asks for "my site's completions".
  prequalification: {
    heading: "Prequalification",
    description: "Everyone who has completed a prequalification on your firm's site.",
  },
  payments: {
    heading: "Payments & ledger",
    description: "Your firm's running balance, scheduled payouts, and statements.",
  },
  "payment-setup": {
    heading: "Payment setup",
    description: "Connect your firm's bank so Phoenix can pay your brokerage.",
  },
  roster: {
    heading: "Sub-brokers",
    description: "Your affiliated sub-brokers and their production.",
  },
  activity: {
    heading: "Activity",
    description: "Recent activity across your firm.",
  },
  requests: {
    heading: "Requests",
    description: "Documents, information, and training for you to complete.",
  },
  documents: {
    heading: "Documents",
    description: "Everything you've uploaded or signed, in one place.",
  },
  files: {
    heading: "Files & library",
    description: "Reference materials and resources we've published for you.",
  },
  notifications: {
    heading: "Notifications",
    description: "Everything we've sent you and everything that needs attention.",
  },
  settings: {
    heading: "Account & settings",
    description: "Manage your profile and password.",
  },
};

function isBrokerageTab(value: string | null): value is BrokerageTab {
  return BROKERAGE_TABS.includes(value as BrokerageTab);
}

export function BrokerageDashboardPage() {
  const auth = usePortalAuth();
  const [params, setParams] = useSearchParams();

  const raw = params.get("tab");
  const tab: BrokerageTab = isBrokerageTab(raw) ? raw : "home";

  const setTab = useCallback(
    (next: PortalTabId) => {
      const updated = new URLSearchParams(params);
      updated.set("tab", next);
      setParams(updated, { replace: true });
    },
    [params, setParams],
  );

  if (!auth.loading && !auth.me) {
    return <Navigate to="/portal" replace />;
  }
  if (auth.me && auth.me.accountType !== "Brokerage") {
    return <Navigate to={dashboardPathFor(auth.me.accountType, auth.me.kind)} replace />;
  }

  const meta = TAB_META[tab];

  return (
    <PortalChrome pageTitle="Brokerage portal" activeTab={tab} onSelectTab={setTab}>
      <div className="space-y-space-5">
        <PageHeader eyebrow="Brokerage portal" title={meta.heading} description={meta.description} />
        {renderBrokerageTabBody(tab)}
      </div>
    </PortalChrome>
  );
}

function renderBrokerageTabBody(tab: BrokerageTab) {
  switch (tab) {
    // Firm data surfaces (PR-6).
    case "home":
      return <BrokerageHomeTab />;
    case "deals":
      return <BrokerageDealsTab />;
    case "prequalification":
      return <PrequalificationTab />;
    case "payments":
      return <BrokeragePaymentsTab />;
    // PR-6b — Stripe payout onboarding (self-serve; the account PR-4's payee
    // resolver blocks on). Its own tab, adjacent to Payments.
    case "payment-setup":
      return <BrokeragePaymentSetupTab />;
    case "roster":
      return <BrokerageRosterTab />;
    // Family-B tabs — scoped to the PortalUser, no firm-level fan-out.
    case "requests":
      return <RequestsTab />;
    case "documents":
      return <DocumentsTab />;
    case "files":
      return <FilesTab />;
    case "notifications":
      return <NotificationsTab />;
    case "settings":
      return <AccountSettingsTab />;
    // PR-10 — the roster's application + deal-submission lifecycle feed.
    case "activity":
      return <BrokerageActivityTab />;
  }
}
